import { useEffect, useState } from "react";

import { MorrowMascot } from "../brand/MorrowMascot";
import { useI18n } from "~/i18n";
import type { MorrowToolkit } from "../../composioConnect";
import { fetchCompanyBrief, parseCompanySituation } from "../../morrowCompanyBrief";
import { writeOnboardingProfile, type MorrowAccountType } from "../../onboardingStore";
import { cn } from "../../lib/utils";
import { appCardForToolkit } from "./morrowLandingCatalog";
import { getMorrowLandingSuggestions, type LandingSuggestion } from "./morrowLandingSuggestions";

/**
 * Post-onboarding empty-chat landing: greeting and a scrollable grid of visual
 * starter cards tied to connected tools.
 */
interface ChatLandingSuggestionsProps {
  readonly onSelect: (text: string) => void;
  readonly accountType: MorrowAccountType | null;
  readonly companyName: string;
  readonly companyWebsite: string;
  readonly companyBrief: string;
  readonly companySituation: string;
  readonly connectedToolkits?: ReadonlySet<string> | undefined;
  readonly onToolkitNudge?: ((toolkit: MorrowToolkit) => void) | undefined;
}

function ToolkitBadge({
  toolkit,
  connected,
}: {
  readonly toolkit: MorrowToolkit;
  readonly connected: boolean;
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  const card = appCardForToolkit(toolkit);
  const Icon = card.icon;

  return (
    <span
      className={cn(
        "inline-flex max-w-[4.25rem] items-center gap-0.5 rounded-full border px-1 py-0.5 text-[9px] font-medium sm:max-w-[5.5rem] sm:gap-1 sm:px-1.5 sm:text-[10px]",
        connected
          ? "border-white/60 bg-white/85 text-foreground"
          : "border-dashed border-white/50 bg-white/60 text-muted-foreground",
      )}
    >
      {logoFailed ? (
        <span
          className="flex size-3.5 shrink-0 items-center justify-center rounded-[3px]"
          style={{ backgroundColor: card.brand }}
        >
          <Icon className="size-2" style={{ color: card.glyph }} aria-hidden="true" />
        </span>
      ) : (
        <img
          src={`https://logos.composio.dev/api/${toolkit}`}
          alt=""
          className="size-3.5 shrink-0 object-contain"
          loading="lazy"
          onError={() => setLogoFailed(true)}
        />
      )}
      <span className="truncate">{card.name}</span>
    </span>
  );
}

function SuggestionCard({
  suggestion,
  index,
  connectedToolkits,
  onSelect,
  onToolkitNudge,
}: {
  readonly suggestion: LandingSuggestion;
  readonly index: number;
  readonly connectedToolkits: ReadonlySet<string>;
  readonly onSelect: (text: string) => void;
  readonly onToolkitNudge?: ((toolkit: MorrowToolkit) => void) | undefined;
}) {
  const Icon = suggestion.icon;
  const toolkit = suggestion.toolkit;
  const armed = toolkit ? connectedToolkits.has(toolkit) : true;

  return (
    <button
      type="button"
      onClick={() => {
        onSelect(suggestion.prompt);
        if (toolkit && !armed) onToolkitNudge?.(toolkit);
      }}
      style={{ animationDelay: `${140 + index * 50}ms` }}
      className="morrow-landing-item button-glass group flex min-w-0 flex-col overflow-hidden rounded-xl border border-border/60 text-left transition-[transform,filter,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-border hover:brightness-[1.02] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div
        className="relative h-[3.75rem] overflow-hidden border-b border-border/30 sm:h-[4.25rem]"
        style={{
          backgroundImage: `linear-gradient(135deg, ${suggestion.visual.from}, ${suggestion.visual.to})`,
        }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-80"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 22%, rgba(255,255,255,0.55), transparent 44%), radial-gradient(circle at 88% 8%, rgba(255,255,255,0.35), transparent 38%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -right-2 -top-2 size-14 rounded-full"
          style={{ backgroundColor: suggestion.visual.wash }}
        />
        <div className="relative flex h-full items-end justify-between gap-1.5 p-1.5 sm:gap-2 sm:p-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-white/70 bg-white shadow-sm sm:size-8">
            <Icon className="size-3.5 text-foreground sm:size-4" aria-hidden="true" />
          </span>
          {toolkit ? <ToolkitBadge toolkit={toolkit} connected={armed} /> : null}
        </div>
      </div>
      <span className="flex flex-col gap-0.5 bg-background/90 px-2 py-1.5 sm:px-2.5 sm:py-2">
        <span className="truncate text-xs font-medium leading-tight text-foreground sm:text-[13px]">
          {suggestion.label}
        </span>
        <span className="line-clamp-1 text-[10px] leading-snug text-muted-foreground sm:text-[11px]">
          {suggestion.description}
        </span>
      </span>
    </button>
  );
}

export function ChatLandingSuggestions({
  onSelect,
  accountType,
  companyName,
  companyWebsite,
  companyBrief,
  companySituation,
  connectedToolkits,
  onToolkitNudge,
}: ChatLandingSuggestionsProps) {
  const { t } = useI18n();
  const greeting = companyName.trim() ? companyName.trim() : "Morrow";
  const profile = {
    accountType,
    companyName,
    companyWebsite,
    companyBrief,
    companySituation: parseCompanySituation(companySituation),
  };
  const suggestions = getMorrowLandingSuggestions(t, profile);
  const toolkitSet = connectedToolkits ?? new Set<string>();

  useEffect(() => {
    const website = companyWebsite.trim();
    if (!website || companyBrief.trim()) return;
    let cancelled = false;
    void fetchCompanyBrief(website).then((lookup) => {
      if (cancelled || !lookup) return;
      writeOnboardingProfile({
        companyBrief: lookup.brief,
        companySituation: lookup.situation,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [companyBrief, companyWebsite]);

  return (
    <div className="pointer-events-auto flex w-full flex-col items-center gap-5">
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="morrow-landing-item">
          <MorrowMascot className="size-14 sm:size-16" />
        </span>
        <div className="flex flex-col items-center gap-1.5">
          <h2 className="morrow-landing-item text-xl font-medium tracking-[-0.02em] text-balance text-foreground sm:text-2xl">
            {t("landing.greeting", { name: greeting })}
          </h2>
          <p className="morrow-landing-item max-w-sm text-balance text-muted-foreground text-xs sm:text-sm">
            {t("landing.subtitle")}
          </p>
        </div>
      </div>

      <div className="morrow-landing-item w-full">
        <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
          {suggestions.map((suggestion, index) => (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              index={index}
              connectedToolkits={toolkitSet}
              onSelect={onSelect}
              {...(onToolkitNudge ? { onToolkitNudge } : {})}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
